import { createSlice, original } from "@reduxjs/toolkit";
import { uniq } from "lodash";
import isNullish from "utils/isNullish";

import { removeCellEntry, setCellEntry } from "./cellMap";
import { toGroupErrorKey } from "./groupDraft";
import { applyDraftCell, toCellKey } from "./reviewDraft";
import { getTermRowId, toValueKey } from "./termRowId";

export const INITIAL_STATE = {
  activeReportId: null,
  report: null,
  categories: null,
  hiddenParagraphs: [],
  isLoadingCategories: false,
  isLoadingTerms: false,
  isReviewMode: false,
  sections: [],
  paragraphs: null,
  sentences: null,
  selectedParagraphIds: [],
  reviewDraft: {},
  reviewErrors: {},
  reviewColumnOps: [],
  reviewColumnErrors: {},
  reviewGroupErrors: {},
};

const CELL_ERROR_KIND = "CELL";
const COLUMN_ERROR_KIND = "COLUMN";
const GROUP_ERROR_KIND = "GROUP";

const toCellErrorTarget = (error) => {
  if (error?.kind !== CELL_ERROR_KIND) {
    return null;
  }
  const columnId = toCellKey(error.columnRef);
  if (isNullish(columnId)) {
    return null;
  }
  return { valueKey: toValueKey(error), columnId };
};

const toColumnErrorKey = (error) =>
  error?.kind === COLUMN_ERROR_KIND ? toCellKey(error.columnRef) : null;

const removeDraftColumn = (cellMap, key) => {
  Object.keys(cellMap).forEach((valueKey) =>
    removeCellEntry(cellMap, valueKey, key)
  );
};

const shreddingDetailSlice = createSlice({
  name: "detail",
  initialState: INITIAL_STATE,
  reducers: {
    reset() {
      return INITIAL_STATE;
    },
    setActiveReportId(state, { payload }) {
      state.activeReportId = isNullish(payload) ? null : Number(payload);
    },
    setReport(state, { payload }) {
      state.report = payload;
    },
    setIsByParagraph(state, { payload }) {
      state.report.isByParagraph = Boolean(payload);
    },
    setCategories(state, { payload }) {
      state.categories = payload || null;
    },
    setHiddenParagraphs(state, { payload }) {
      state.hiddenParagraphs = uniq(payload || []);
    },
    setIsLoadingCategories(state, { payload }) {
      state.isLoadingCategories = Boolean(payload);
    },
    setSections(state, { payload }) {
      state.sections = payload || [];
    },
    setIsReviewMode: (state, action) => {
      state.isReviewMode = action.payload;
    },
    setTerms(state, { payload }) {
      if (state.report?.isByParagraph) {
        state.paragraphs = payload || null;
      } else {
        state.sentences = payload || null;
      }
    },
    setParagraphs(state, { payload }) {
      state.paragraphs = payload || null;
    },
    setSentences(state, { payload }) {
      state.sentences = payload || null;
    },
    setSelectedParagraphIds: (state, { payload }) => {
      state.selectedParagraphIds = payload || [];
    },
    setDraftCells(state, { payload }) {
      (payload || []).forEach((cell) => {
        applyDraftCell(state.reviewDraft, cell);
        removeCellEntry(state.reviewErrors, cell.valueKey, cell.columnId);
      });
    },
    clearDraft(state) {
      state.reviewDraft = {};
      state.reviewErrors = {};
      state.reviewColumnOps = [];
      state.reviewColumnErrors = {};
      state.reviewGroupErrors = {};
    },
    setReviewColumnOps(state, { payload }) {
      state.reviewColumnOps = payload || [];
    },
    clearColumnError(state, { payload }) {
      delete state.reviewColumnErrors[payload];
    },
    clearGroupError(state, { payload }) {
      delete state.reviewGroupErrors[payload];
    },
    dropDraftColumn(state, { payload }) {
      removeDraftColumn(state.reviewDraft, payload);
      removeDraftColumn(state.reviewErrors, payload);
      delete state.reviewColumnErrors[payload];
    },
    seedDraftColumn(state, { payload: { fromKey, toKey } }) {
      Object.values(state.reviewDraft).forEach((rowDraft) => {
        if (fromKey in rowDraft) {
          rowDraft[toKey] = rowDraft[fromKey];
        }
      });
    },
    reportReloadedAfterReview(state, { payload }) {
      state.report = payload;
      state.reviewColumnOps = [];
      state.reviewColumnErrors = {};
      state.reviewGroupErrors = {};
    },
    setReviewErrors(state, { payload }) {
      state.reviewErrors = {};
      state.reviewColumnErrors = {};
      state.reviewGroupErrors = {};
      (payload || []).forEach((error) => {
        if (error?.kind === GROUP_ERROR_KIND) {
          state.reviewGroupErrors[toGroupErrorKey(error.columnRef)] =
            error.code;
          return;
        }
        const columnErrorKey = toColumnErrorKey(error);
        if (!isNullish(columnErrorKey)) {
          state.reviewColumnErrors[columnErrorKey] = error.code;
          return;
        }
        const target = toCellErrorTarget(error);
        if (target) {
          setCellEntry(
            state.reviewErrors,
            target.valueKey,
            target.columnId,
            error.code
          );
        }
      });
    },
    updateTerms: (state, action) => {
      const { ids = [], changes = {} } = action.payload || {};

      if (!Array.isArray(ids) || ids.length === 0) return;

      const isByParagraph = state.report.isByParagraph;
      const terms = isByParagraph ? state.paragraphs : state.sentences;
      const idsToUpdate = new Set(ids);

      original(terms).forEach((term, index) => {
        if (idsToUpdate.has(getTermRowId(term, isByParagraph))) {
          Object.assign(terms[index], changes);
        }
      });
    },
  },
});

export const {
  clearColumnError,
  clearGroupError,
  clearDraft,
  dropDraftColumn,
  reportReloadedAfterReview,
  seedDraftColumn,
  setDraftCells,
  setReviewColumnOps,
  setReviewErrors,
  setIsByParagraph,
  reset,
  setActiveReportId,
  setReport,
  setCategories,
  setHiddenParagraphs,
  setIsLoadingCategories,
  setIsReviewMode,
  setSections,
  setTerms,
  setParagraphs,
  setSentences,
  setSelectedParagraphIds,
  updateTerms,
} = shreddingDetailSlice.actions;

export default shreddingDetailSlice;
